import maxBy from 'lodash/maxBy'
import { CalendarQuarter } from '@klear/date/calendar-quarter'
import { isPreviewableContentType } from '@klear/upload/content-type'
import type { DocumentProcessingStatus, DocumentReviewReason, ReviewerHints } from '@klear/schema/document-processing'
import type { DocumentReviewDoubt, DocumentReviewProposal } from '@klear/schema/document-review'
import type {
  DocumentArrivalVia,
  DocumentStatus,
  FinancialPeriodEnd,
  ORDER_EVENT_DOCUMENT_TYPES
} from '@klear/schema/documents'
import type { DocumentType } from '@klear/constants/document-types'
import type { PagedRows } from '@klear/schema/wire/paging'
import { Timestamp } from '@klear/time'

export type OrderEventDocumentType = (typeof ORDER_EVENT_DOCUMENT_TYPES)[number]

export type DocumentReference =
  | {
      kind: 'order_event'
      type: OrderEventDocumentType
      id: number
      fileId: string
      displayId: string
      orderId: number
      orderDisplayId: string | null
    }
  | { kind: 'payment'; id: number; displayId: string }
  | { kind: 'financial_statement'; id: number; periodEnd: FinancialPeriodEnd }

export type TraderDocumentJson = {
  id: number
  traderId: number
  name: string
  type: DocumentType
  status: DocumentStatus
  reviewReason: DocumentReviewReason | null
  contentType: string
  byteSize: number
  notes: string
  references: DocumentReference[]
  arrivedAt: number
  arrivedVia: DocumentArrivalVia
}

export class TraderDocument {
  static readonly __klearClass = 'TraderDocument'

  id: number
  traderId: number
  name: string
  type: DocumentType
  status: DocumentStatus
  reviewReason: DocumentReviewReason | null
  contentType: string
  byteSize: number
  notes: string
  references: DocumentReference[]
  arrivedAt: Timestamp
  arrivedVia: DocumentArrivalVia

  constructor(props: TraderDocumentJson) {
    this.id = props.id
    this.traderId = props.traderId
    this.name = props.name
    this.type = props.type
    this.status = props.status
    this.reviewReason = props.reviewReason
    this.contentType = props.contentType
    this.byteSize = props.byteSize
    this.notes = props.notes
    this.references = props.references
    this.arrivedAt = new Timestamp(props.arrivedAt)
    this.arrivedVia = props.arrivedVia
  }

  toJSON(): TraderDocumentJson {
    return {
      id: this.id,
      traderId: this.traderId,
      name: this.name,
      type: this.type,
      status: this.status,
      reviewReason: this.reviewReason,
      contentType: this.contentType,
      byteSize: this.byteSize,
      notes: this.notes,
      references: this.references,
      arrivedAt: this.arrivedAt.epochMs,
      arrivedVia: this.arrivedVia
    }
  }

  get periodEnd(): CalendarQuarter | null {
    const ends = this.references.flatMap((reference) =>
      reference.kind === 'financial_statement' ? [CalendarQuarter.of(reference.periodEnd)] : []
    )
    return maxBy(ends, (end) => end.year * 4 + end.quarter) ?? null
  }

  get isPdf(): boolean {
    return this.contentType === 'application/pdf'
  }

  get isImage(): boolean {
    return !this.isPdf && isPreviewableContentType(this.contentType)
  }
}

export type DocumentArrival = {
  sender: string | null
  inboundEmailId: number | null
}

export type DocumentReviewRunSummary = {
  id: string
  run: number
  status: DocumentProcessingStatus
  reviewedAt: Timestamp | null
}

export type DocumentAllowedAction = 'update' | 'remove' | 'file' | 'dismiss'

export type DocumentListRow = {
  document: TraderDocument
  latestRun: DocumentReviewRunSummary | null
  allowedActions: DocumentAllowedAction[]
  removeBlockedReason: string | null
}

export type DocumentReviewDecision = {
  reviewedBy: number | null
  filedAutomatically: boolean
  answers: NonNullable<ReviewerHints['answers']>
}

export type DocumentDetail = DocumentListRow & {
  arrival: DocumentArrival
  runs: DocumentReviewRunSummary[]
  proposals: DocumentReviewProposal[] | null
  questions: DocumentReviewDoubt[] | null
  decision: DocumentReviewDecision | null
}

export type DocumentStatusCounts = Partial<Record<DocumentStatus, number>> & { all: number }

export type DocumentList = PagedRows<DocumentListRow> & { statusCounts: DocumentStatusCounts }

export type DocumentRunOutcome = 'queued' | 'deduped' | 'not_needed' | 'disabled'

export type DocumentUpdateResult = { document: DocumentDetail | null; run: DocumentRunOutcome }
